/**
 * useDashboardConfig — loads and persists per-user dashboard config
 * via GET/PUT /api/v2/admin/me/dashboard-config.
 *
 * On load: fetches stored config. On save: debounced PUT (500 ms) so rapid
 * drag/resize operations don't flood the API.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePermissions } from '../contexts/PermissionsContext';
import {
  dashboardConfigApi,
  dashboardAreaApi,
  DASHBOARD_DEFAULTS_PERMISSION,
  type DashboardSource,
  type DashboardConfig,
  type DashboardLayoutItem,
  type DashboardWidgetSettings,
} from './api';

export const DEFAULT_WIDGET_SETTINGS: DashboardWidgetSettings = {
  visible: true,
  mode: 'full',
};

export interface UseDashboardConfigReturn {
  config: DashboardConfig;
  loading: boolean;
  /** Update the grid layout (position + size) and persist. */
  setLayout: (layout: DashboardLayoutItem[]) => void;
  /** Toggle a widget on/off or change its display mode, then persist. */
  setWidgetSettings: (id: string, settings: Partial<DashboardWidgetSettings>) => void;
  /** Read settings for a single widget (falls back to default). */
  widgetSettings: (id: string) => DashboardWidgetSettings;
  /** Paket 2310e, Bereich "start": woher das Layout stammt (user | default | builtin). */
  source: DashboardSource;
  /** Darf der Admin den Bereichs-Standard setzen/entfernen? */
  canSetDefault: boolean;
  /** Eigenes Layout verwerfen: Bereichs-Standard bzw. eingebauter Default greift wieder. */
  resetToDefault: () => Promise<void>;
  /** Aktuelles Layout als Standard für alle im Bereich speichern. */
  saveAsDefault: () => Promise<void>;
  /** Bereichs-Standard entfernen. */
  clearDefault: () => Promise<void>;
}

/** Gleiche Positionen/Größen je Widget-ID (Reihenfolge egal). */
export function sameLayout(a: DashboardLayoutItem[] | null | undefined, b: DashboardLayoutItem[] | null | undefined): boolean {
  if (!a || !b || a.length !== b.length) return false;
  const byId = new Map(b.map(l => [l.i, l]));
  return a.every(l => {
    const o = byId.get(l.i);
    return !!o && o.x === l.x && o.y === l.y && o.w === l.w && o.h === l.h;
  });
}

/**
 * @param effectiveLayout  Optional: bildet ein gespeichertes (ggf. leeres) Layout auf das
 *   tatsächlich angezeigte ab. Der Grid meldet beim reinen Laden genau dieses Layout über
 *   onLayoutChange; das darf NICHT als Nutzeränderung gespeichert werden (sonst legt jeder
 *   Besuch ein eigenes User-Layout an und der Bereichs-Standard greift nie).
 */
export function useDashboardConfig(
  effectiveLayout?: (stored: DashboardLayoutItem[] | null | undefined) => DashboardLayoutItem[],
): UseDashboardConfigReturn {
  const [config, setConfig] = useState<DashboardConfig>({});
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState<DashboardSource>('builtin');
  const { hasPermission } = usePermissions();
  const canSetDefault = hasPermission(DASHBOARD_DEFAULTS_PERMISSION);

  // Debounce timer ref — cleared on each save call.
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Bereich "start": User-Layout > Bereichs-Standard > eingebauter Default.
        const stored = await dashboardAreaApi.get('start');
        if (alive) { setConfig(stored.config); setSource(stored.source); }
      } catch {
        // Non-fatal — use default empty config; user can still drag/toggle
        // and the next save will create the row.
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const scheduleSave = useCallback((next: DashboardConfig) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      dashboardConfigApi.save(next).then(() => setSource('user')).catch(() => {
        // Ignore save errors silently — layout is still applied in memory.
      });
    }, 500);
  }, []);

  const effectiveRef = useRef(effectiveLayout);
  effectiveRef.current = effectiveLayout;

  const setLayout = useCallback((layout: DashboardLayoutItem[]) => {
    setConfig(prev => {
      const current = effectiveRef.current ? effectiveRef.current(prev.layout) : prev.layout;
      if (sameLayout(layout, current)) return prev; // keine echte Änderung: weder State noch Speichern
      const next = { ...prev, layout };
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const setWidgetSettings = useCallback((id: string, settings: Partial<DashboardWidgetSettings>) => {
    setConfig(prev => {
      const existing = prev.widgets?.[id] ?? { ...DEFAULT_WIDGET_SETTINGS };
      const next = {
        ...prev,
        widgets: {
          ...(prev.widgets ?? {}),
          [id]: { ...existing, ...settings },
        },
      };
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const widgetSettings = useCallback((id: string): DashboardWidgetSettings => {
    return config.widgets?.[id] ?? { ...DEFAULT_WIDGET_SETTINGS };
  }, [config.widgets]);

  const resetToDefault = useCallback(async () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    const res = await dashboardAreaApi.reset('start');
    setConfig(res.config);
    setSource(res.source);
  }, []);

  const saveAsDefault = useCallback(async () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    await dashboardAreaApi.setDefault('start', { layout: config.layout ?? [], widgets: config.widgets ?? {} });
  }, [config.layout, config.widgets]);

  const clearDefault = useCallback(async () => {
    await dashboardAreaApi.clearDefault('start');
  }, []);

  return {
    config, loading, setLayout, setWidgetSettings, widgetSettings,
    source, canSetDefault, resetToDefault, saveAsDefault, clearDefault,
  };
}
